import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { HandCoins, Plus } from "lucide-react";
import { api } from "@/lib/api-client";
import { queryClient } from "@/lib/query-client";
import { currency, shortDate, todayIso } from "@/lib/format";
import { statusClass, statusLabel } from "@/lib/status";
import { useAppStore } from "@/store/app-store";
import { Button } from "@/components/ui/Button";
import { DateInput } from "@/components/ui/DateInput";
import { LoadingGrid, StateView } from "@/components/ui/StateView";

type Settlement = {
  id: string;
  periodStart: string;
  periodEnd: string;
  totalBookingAmount: string;
  platformCollectedAmount: string;
  hotelCollectedAmount: string;
  commissionAmount: string;
  payableAmount: string;
  status: "PENDING" | "PAID";
  paidOn: string | null;
  createdAt: string;
  stayProfile: { id: string; name: string } | null;
  owner: { id: string; name: string; email: string };
  items: { id: string; grossAmount: string; commissionAmount: string; payableAmount: string; booking: { bookingRef: string; guestName: string; checkInDate: string; checkOutDate: string } }[];
};

const money = (value: string | number) => currency.format(Number(value));

// Settlements: what the platform owes the owner for money guests paid in the app (minus commission). Cash
// taken at the hotel is already with the owner and is shown for the record. Admins create settlements and
// mark them paid; owners see theirs read-only.
export function SettlementsPage() {
  const role = useAppStore((state) => state.user?.role);
  const activeProperty = useAppStore((state) => state.activeProperty!);
  const activeOwner = useAppStore((state) => state.activeOwner);
  const isAdmin = role === "ADMIN";
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [confirmPaid, setConfirmPaid] = useState<Settlement | null>(null);
  const query = useQuery({
    queryKey: ["settlements", activeProperty.id],
    queryFn: () => api<Settlement[]>(`/settlements?propertyId=${activeProperty.id}`),
  });
  const markPaid = useMutation({
    mutationFn: (id: string) => api<Settlement>(`/settlements/${id}/paid`, { method: "PATCH" }),
    onSuccess: async () => {
      setConfirmPaid(null);
      await queryClient.invalidateQueries({ queryKey: ["settlements"] });
    },
  });

  if (query.isLoading) return <main className="page"><LoadingGrid /></main>;
  if (query.isError) return <main className="page"><StateView title="Couldn't load settlements" message="Check your internet connection and try again." action={() => void query.refetch()} /></main>;
  const settlements = query.data ?? [];

  return (
    <main className="page settlements-page">
      <header className="page-head resource-head">
        <div>
          <h1>Settlements</h1>
          <p className="page-head__meta">App payments paid out to the owner, minus commission</p>
        </div>
        {isAdmin && !creating && <Button onClick={() => setCreating(true)}><Plus size={18} /> New settlement</Button>}
      </header>

      {isAdmin && creating && activeOwner && (
        <NewSettlementForm ownerId={activeOwner.id} ownerName={activeOwner.name} propertyId={activeProperty.id} propertyName={activeProperty.name} onDone={() => setCreating(false)} />
      )}

      {!settlements.length ? (
        <div className="card"><div className="mini-empty"><HandCoins /><p>No settlements yet.{isAdmin ? " Create one for a period with New settlement." : ""}</p></div></div>
      ) : (
        <div className="settlement-list">
          {settlements.map((settlement) => (
            <article className="card settlement-card" key={settlement.id}>
              <button type="button" className="settlement-card__head" aria-expanded={openId === settlement.id} onClick={() => setOpenId(openId === settlement.id ? null : settlement.id)}>
                <span>
                  <strong className="num">{shortDate(settlement.periodStart)} – {shortDate(settlement.periodEnd)}</strong>
                  <small>{settlement.stayProfile?.name ?? "All properties"} · {settlement.items.length} booking(s)</small>
                </span>
                <span className={statusClass(settlement.status)}>{statusLabel(settlement.status)}</span>
              </button>
              <dl className="settlement-card__figures">
                <div><dt>Paid in app</dt><dd className="num">{money(settlement.platformCollectedAmount)}</dd></div>
                <div><dt>Commission</dt><dd className="num">-{money(settlement.commissionAmount)}</dd></div>
                <div className="settlement-card__payable"><dt>Payable to owner</dt><dd className="num">{money(settlement.payableAmount)}</dd></div>
                <div><dt>Collected at hotel</dt><dd className="num">{money(settlement.hotelCollectedAmount)}</dd></div>
              </dl>
              {settlement.status === "PAID" && settlement.paidOn && <p className="settlement-card__note">Paid on {shortDate(settlement.paidOn)}</p>}
              {openId === settlement.id && (
                <div className="table-wrap">
                  {!settlement.items.length ? <p className="settlement-card__note">No app payments in this period - nothing to pay out.</p> : (
                    <table className="data-table">
                      <thead><tr><th scope="col">Booking</th><th scope="col">Stay</th><th scope="col" className="num-col">Paid in app</th><th scope="col" className="num-col">Commission</th><th scope="col" className="num-col">Payable</th></tr></thead>
                      <tbody>
                        {settlement.items.map((item) => (
                          <tr key={item.id}>
                            <td><strong>{item.booking.bookingRef}</strong><small>{item.booking.guestName}</small></td>
                            <td className="num">{shortDate(item.booking.checkInDate)} → {shortDate(item.booking.checkOutDate)}</td>
                            <td className="num-col num">{money(item.grossAmount)}</td>
                            <td className="num-col num">-{money(item.commissionAmount)}</td>
                            <td className="num-col num"><strong>{money(item.payableAmount)}</strong></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
              {isAdmin && settlement.status === "PENDING" && (
                confirmPaid?.id === settlement.id ? (
                  <div className="bd-confirm" role="alertdialog">
                    <p><strong>Mark this settlement as paid?</strong> {money(settlement.payableAmount)} to {settlement.owner.name}. This can't be undone.</p>
                    {markPaid.error && <div className="form-error">{markPaid.error.message}</div>}
                    <div className="bd-actions__row">
                      <Button variant="secondary" disabled={markPaid.isPending} onClick={() => setConfirmPaid(null)}>Go back</Button>
                      <Button loading={markPaid.isPending} onClick={() => markPaid.mutate(settlement.id)}>Yes, mark paid</Button>
                    </div>
                  </div>
                ) : <Button variant="secondary" onClick={() => setConfirmPaid(settlement)}>Mark as paid</Button>
              )}
            </article>
          ))}
        </div>
      )}
    </main>
  );
}

function NewSettlementForm({ ownerId, ownerName, propertyId, propertyName, onDone }: { ownerId: string; ownerName: string; propertyId: string; propertyName: string; onDone: () => void }) {
  const today = todayIso();
  const [periodStart, setPeriodStart] = useState(`${today.slice(0, 8)}01`);
  const [periodEnd, setPeriodEnd] = useState(today);
  const [scope, setScope] = useState<"property" | "owner">("property");
  const [commissionPercent, setCommissionPercent] = useState("0");
  const [confirming, setConfirming] = useState(false);
  const create = useMutation({
    mutationFn: () => api<Settlement>("/settlements/generate", {
      method: "POST",
      body: { ownerId, stayProfileId: scope === "property" ? propertyId : undefined, periodStart, periodEnd, commissionPercent: Number(commissionPercent || 0) },
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["settlements"] });
      onDone();
    },
    onSettled: () => setConfirming(false),
  });
  const valid = Boolean(periodStart && periodEnd && periodStart <= periodEnd) && Number(commissionPercent) >= 0 && Number(commissionPercent) <= 100;

  return (
    <section className="card booking-inline-form settlement-form">
      <h4>New settlement - {ownerName}</h4>
      <div className="property-form-grid">
        <label>From<DateInput max={periodEnd} value={periodStart} onChange={(event) => setPeriodStart(event.target.value)} /></label>
        <label>To<DateInput min={periodStart} max={today} value={periodEnd} onChange={(event) => setPeriodEnd(event.target.value)} /></label>
        <label>For
          <select value={scope} onChange={(event) => setScope(event.target.value as "property" | "owner")}>
            <option value="property">{propertyName} only</option>
            <option value="owner">All of {ownerName}'s properties</option>
          </select>
        </label>
        <label>Commission (% of app payments)<input type="number" min={0} max={100} step="0.01" value={commissionPercent} onChange={(event) => setCommissionPercent(event.target.value)} /></label>
      </div>
      <div className="form-hint">Uses payments made in this period. Commission applies only to money paid in the app; cash taken at the hotel is already with the owner.</div>
      {create.error && <div className="form-error">{create.error.message}</div>}
      {confirming ? (
        <div className="bd-confirm" role="alertdialog">
          <p><strong>Create this settlement?</strong> {periodStart} to {periodEnd}, {scope === "property" ? propertyName : "all properties"}, commission {Number(commissionPercent || 0)}%.</p>
          <div className="bd-actions__row">
            <Button variant="secondary" disabled={create.isPending} onClick={() => setConfirming(false)}>Go back</Button>
            <Button loading={create.isPending} onClick={() => create.mutate()}>Yes, create</Button>
          </div>
        </div>
      ) : (
        <div className="booking-inline-form__actions">
          <Button type="button" variant="ghost" onClick={onDone}>Cancel</Button>
          <Button type="button" disabled={!valid} onClick={() => setConfirming(true)}>Continue</Button>
        </div>
      )}
    </section>
  );
}
